import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { useState, useEffect } from 'react';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EventIcon from '@mui/icons-material/Event';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AppLayout from '../components/AppLayout';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS, EXPENSE_CLAIMS_ENABLED } from '../constants/api';
import { formatDate } from '../utils/dateFormat';
import type { MyOutstandingTasks } from '../types';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad } from '../utils/userFacingCopy';
import { checkInFrequencyConfirmCopy } from '../utils/checkInOptOutCopy';

export default function MyTasksPage() {
  const navigate = useNavigate();
  const { updateOutstandingTasksCount } = useAuth();
  const [data, setData] = useState<MyOutstandingTasks | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await apiService.get<MyOutstandingTasks>(API_ENDPOINTS.USERS.OUTSTANDING_TASKS);
        setData(response.data);
        updateOutstandingTasksCount(response.data?.totalCount ?? 0);
      } catch (err: unknown) {
                setError(getApiErrorMessage(err, couldNotLoad('your tasks')));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [updateOutstandingTasksCount]);

  if (loading) {
    return (
      <AppLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  if (error && !data) {
    return (
      <AppLayout>
        <Box sx={{ maxWidth: '1400px', mx: 'auto', p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <TaskAltIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
            <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0 }}>
              Tasks
            </Typography>
          </Box>
          <Alert severity="error">{error}</Alert>
        </Box>
      </AppLayout>
    );
  }

  const totalCount = data?.totalCount ?? 0;

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto', p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <TaskAltIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
          <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0 }}>
            Tasks
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Your personal to-do list — reviews, check-ins, approvals for your direct reports, and more. Use Team and Organisation pages when you need hierarchy-wide or company-wide views.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {data && totalCount === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: { xs: '50vh', sm: '60vh' },
              py: 6,
              px: 2,
            }}
          >
            <CheckCircleIcon
              sx={{
                fontSize: { xs: 120, sm: 160, md: 200 },
                color: 'success.main',
                mb: 2,
                filter: 'drop-shadow(0 4px 12px rgba(76, 175, 80, 0.35))',
              }}
            />
            <Typography
              variant="h4"
              component="p"
              fontWeight="700"
              color="text.primary"
              textAlign="center"
              gutterBottom
              sx={{ fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' } }}
            >
              You're all caught up!
            </Typography>
            <Typography variant="body1" color="text.secondary" textAlign="center" maxWidth="360px">
              There's nothing that needs your action right now.
            </Typography>
          </Box>
        )}

        {data && totalCount > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Self-review pending */}
            {data.selfReviewPending.length > 0 && (
              <Card variant="outlined" sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight="600">
                      Self-reviews pending ({data.selfReviewPending.length})
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Cycle</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.selfReviewPending.map((task) => (
                          <TableRow key={task.reviewId} hover>
                            <TableCell>{task.reviewCycleName}</TableCell>
                            <TableCell>
                              <Chip label={task.status} size="small" color={task.status === 'NotStarted' ? 'error' : 'warning'} />
                            </TableCell>
                            <TableCell align="right">
                              <Button size="small" endIcon={<OpenInNewIcon />} onClick={() => navigate(`/reviews/${task.reviewId}`)} sx={{ textTransform: 'none' }}>
                                View review
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Manager review pending */}
            {data.managerReviewPending.length > 0 && (
              <Card variant="outlined" sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <GroupIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight="600">
                      Manager reviews pending ({data.managerReviewPending.length})
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Employee</strong></TableCell>
                          <TableCell><strong>Cycle</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.managerReviewPending.map((task) => (
                          <TableRow key={task.reviewId} hover>
                            <TableCell>{task.employeeName}</TableCell>
                            <TableCell>{task.reviewCycleName}</TableCell>
                            <TableCell><Chip label={task.status} size="small" color="warning" /></TableCell>
                            <TableCell align="right">
                              <Button size="small" endIcon={<OpenInNewIcon />} onClick={() => navigate(`/reviews/${task.reviewId}`)} sx={{ textTransform: 'none' }}>
                                View review
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Peer feedback requested */}
            {data.peerFeedbackPending.length > 0 && (
              <Card variant="outlined" sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <GroupIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight="600">
                      Peer feedback requested ({data.peerFeedbackPending.length})
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Employee (reviewee)</strong></TableCell>
                          <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.peerFeedbackPending.map((task) => (
                          <TableRow key={`${task.reviewId}-${task.peerId}`} hover>
                            <TableCell>{task.employeeName}</TableCell>
                            <TableCell align="right">
                              <Button size="small" endIcon={<OpenInNewIcon />} onClick={() => navigate(`/reviews/${task.reviewId}/peer-review`)} sx={{ textTransform: 'none' }}>
                                Give feedback
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Team peer feedback outstanding — manager chase list */}
            {(data.teamPeerFeedbackPending?.length ?? 0) > 0 && (
              <Card variant="outlined" sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <GroupIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight="600">
                      Peer feedback outstanding on your team ({data.teamPeerFeedbackPending!.length})
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Invited peers who have not submitted yet on reviews you manage. Chase them so feedback is in before you submit for discussion.
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Peer reviewer</strong></TableCell>
                          <TableCell><strong>Email</strong></TableCell>
                          <TableCell><strong>Review for</strong></TableCell>
                          <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.teamPeerFeedbackPending!.map((task) => (
                          <TableRow key={`${task.reviewId}-${task.peerId}`} hover>
                            <TableCell>{task.peerName}</TableCell>
                            <TableCell>
                              {task.peerEmail ? (
                                <Button
                                  size="small"
                                  href={`mailto:${task.peerEmail}`}
                                  sx={{ textTransform: 'none', minWidth: 0, p: 0, justify: 'inline', verticalAlign: 'baseline' }}
                                >
                                  {task.peerEmail}
                                </Button>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell>{task.employeeName}</TableCell>
                            <TableCell align="right">
                              <Button size="small" endIcon={<OpenInNewIcon />} onClick={() => navigate(`/reviews/${task.reviewId}`)} sx={{ textTransform: 'none' }}>
                                Open review
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Check-ins needing your action */}
            {data.checkInTasks.length > 0 && (
              <Card variant="outlined" sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <EventIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight="600">
                      Check-ins needing your action ({data.checkInTasks.length})
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Context</strong></TableCell>
                          <TableCell><strong>Due date</strong></TableCell>
                          <TableCell><strong>Task type</strong></TableCell>
                          <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.checkInTasks.map((task) => (
                          <TableRow key={task.checkInId} hover>
                            <TableCell>{task.employeeName} / {task.managerName}</TableCell>
                            <TableCell>{formatDate(task.dueDate)}</TableCell>
                            <TableCell>
                              <Chip
                                label={task.taskKind === 'ManagerRecordDecision' ? 'Record decision' : 'Acknowledge occurrence'}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Button size="small" endIcon={<OpenInNewIcon />} onClick={() => navigate(`/checkins/${task.checkInId}`)} sx={{ textTransform: 'none' }}>
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Confirm check-in frequency */}
            {data.checkInFrequencyNotSet.length > 0 && (
              <Card variant="outlined" sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ScheduleIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight="600">
                      {checkInFrequencyConfirmCopy.tasksTitle(data.checkInFrequencyNotSet.length)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {checkInFrequencyConfirmCopy.tasksBody}
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Team member</strong></TableCell>
                          <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.checkInFrequencyNotSet.map((task) => (
                          <TableRow key={task.employeeId} hover>
                            <TableCell>{task.employeeName}</TableCell>
                            <TableCell align="right">
                              <Button size="small" endIcon={<OpenInNewIcon />} onClick={() => navigate('/team-roster')} sx={{ textTransform: 'none' }}>
                                {checkInFrequencyConfirmCopy.tasksAction}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Leave approvals */}
            {data.leaveApprovalPending.length > 0 && (
              <Card variant="outlined" sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <EventIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight="600">
                      Leave approvals ({data.leaveApprovalPending.length})
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Requester</strong></TableCell>
                          <TableCell><strong>Leave type</strong></TableCell>
                          <TableCell><strong>Dates</strong></TableCell>
                          <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.leaveApprovalPending.map((task) => (
                          <TableRow key={task.id} hover>
                            <TableCell>{task.requesterName ?? 'Unknown'}</TableCell>
                            <TableCell>{task.leaveTypeName ?? '—'}</TableCell>
                            <TableCell>{formatDate(task.fromDate)} – {formatDate(task.toDate)}</TableCell>
                            <TableCell align="right">
                              <Button size="small" endIcon={<OpenInNewIcon />} onClick={() => navigate('/team-leave')} sx={{ textTransform: 'none' }}>
                                View / Approve
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Expense approvals */}
            {EXPENSE_CLAIMS_ENABLED && data.expenseApprovalPending?.length > 0 && (
              <Card variant="outlined" sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ReceiptIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight="600">
                      Expense approvals ({data.expenseApprovalPending.length})
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Requester</strong></TableCell>
                          <TableCell align="right"><strong>Total</strong></TableCell>
                          <TableCell><strong>Submitted</strong></TableCell>
                          <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.expenseApprovalPending.map((task) => (
                          <TableRow key={task.claimId} hover>
                            <TableCell>{task.requesterName}</TableCell>
                            <TableCell align="right">£{task.total.toFixed(2)}</TableCell>
                            <TableCell>{formatDate(task.submittedAtUtc, { includeTime: true })}</TableCell>
                            <TableCell align="right">
                              <Button size="small" endIcon={<OpenInNewIcon />} onClick={() => navigate('/team-expenses')} sx={{ textTransform: 'none' }}>
                                View / Approve
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* IDP approvals */}
            {data.idpApprovalPending.length > 0 && (
              <Card variant="outlined" sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AssignmentIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight="600">
                      IDP approvals ({data.idpApprovalPending.length})
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Employee</strong></TableCell>
                          <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.idpApprovalPending.map((task) => (
                          <TableRow key={task.idpId} hover>
                            <TableCell>{task.employeeName}</TableCell>
                            <TableCell align="right">
                              <Button size="small" endIcon={<OpenInNewIcon />} onClick={() => navigate(`/individual-development-plan?pending-approvals=true`)} sx={{ textTransform: 'none' }}>
                                View IDP
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Role template acknowledgment */}
            {data.roleTemplateAcknowledgmentPending.length > 0 && (
              <Card variant="outlined" sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight="600">
                      Role template acknowledgment ({data.roleTemplateAcknowledgmentPending.length})
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Team member</strong></TableCell>
                          <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.roleTemplateAcknowledgmentPending.map((task) => (
                          <TableRow key={task.userId} hover>
                            <TableCell>{task.userName}</TableCell>
                            <TableCell align="right">
                              <Button size="small" endIcon={<OpenInNewIcon />} onClick={() => navigate('/team-roster')} sx={{ textTransform: 'none' }}>
                                Review
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}
          </Box>
        )}
      </Box>
    </AppLayout>
  );
}
